import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Settings as SettingsIcon, Users, TrendingUp, TrendingDown, Wallet, Bell, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DayDetail from './DayDetail';
import SettingsModal from './SettingsModal';
import JointAccountDialog from './JointAccountDialog';
import JointAccountTour from './JointAccountTour';
import FloatingActionButton from './FloatingActionButton';
import OperationForm from './OperationForm';
import RecurringOperationForm from './RecurringOperationForm';
import NotificationCenter from './NotificationCenter';
import BilanMensuel from './BilanMensuel';
import HistoriqueMensuel from './HistoriqueMensuel';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const BudgetCalendar = ({ calendar, user, onLogout, onViewFullHistory }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [monthSummary, setMonthSummary] = useState(null);
  const [showDayDetail, setShowDayDetail] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showJointAccount, setShowJointAccount] = useState(false);
  const [showJointTour, setShowJointTour] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [loading, setLoading] = useState(true);
  const [slideDirection, setSlideDirection] = useState(0);
  
  // FAB related states
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddType, setQuickAddType] = useState(null); // 'credit', 'debit', 'recurring'
  
  // Swipe gesture states
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;
  
  const calendarRef = useRef(null);

  // Load unread notification count
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const response = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
        setUnreadNotifications(response.data.unread_count || 0);
      } catch (error) {
        console.error('Error loading unread count:', error);
      }
    };
    loadUnreadCount();
    
    // Refresh every 5 minutes
    const interval = setInterval(loadUnreadCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Check if should show joint account tour
  useEffect(() => {
    if (calendar?.type === 'joint') {
      const tourKey = `joint_tour_${calendar.id}`;
      const hasSeenTour = localStorage.getItem(tourKey);
      if (!hasSeenTour) {
        setTimeout(() => setShowJointTour(true), 500);
      }
    }
  }, [calendar]);

  const handleTourComplete = () => {
    if (calendar?.id) {
      localStorage.setItem(`joint_tour_${calendar.id}`, 'true');
    }
    setShowJointTour(false);
  };

  // Auto-refresh for joint calendars
  useEffect(() => {
    if (calendar && calendar.type === 'joint') {
      const interval = setInterval(loadMonthData, 15000);
      return () => clearInterval(interval);
    }
  }, [calendar, currentDate]);

  useEffect(() => {
    if (calendar) {
      loadMonthData();
    }
  }, [currentDate, calendar]);

  const loadMonthData = async () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;

    try {
      const response = await axios.get(
        `${API}/month-summary/${year}/${month}?calendar_id=${calendar.id}`,
        { withCredentials: true }
      );
      setMonthSummary(response.data);
    } catch (error) {
      console.error('Error loading month data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return { 
      daysInMonth: lastDay.getDate(), 
      startingDayOfWeek: firstDay.getDay() 
    };
  };

  const handleMonthChange = useCallback((direction) => {
    setSlideDirection(direction);
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + direction));
  }, []);

  const handlePrevMonth = () => handleMonthChange(-1);
  const handleNextMonth = () => handleMonthChange(1);

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) handleNextMonth();
    if (distance < -minSwipeDistance) handlePrevMonth();
  };

  const handleDayClick = (day) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const dateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setShowDayDetail(true);
  };

  const getDailyData = (day) => {
    if (!monthSummary) return null;
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const dateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return monthSummary.daily_balances.find(d => d.date === dateStr);
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isPast = (day) => {
    const today = new Date();
    const cellDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return cellDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
  const { daysInMonth, startingDayOfWeek } = getDaysInMonth();

  // Calculate balance color and status
  const getBalanceStyle = (balance) => {
    if (balance === undefined || balance === null) return { color: 'text-gray-400', bg: '' };
    if (balance >= 100) return { color: 'text-emerald-600', bg: 'bg-emerald-50' };
    if (balance >= 0) return { color: 'text-teal-600', bg: 'bg-teal-50' };
    if (balance >= -50) return { color: 'text-amber-600', bg: 'bg-amber-50' };
    return { color: 'text-rose-600', bg: 'bg-rose-50' };
  };

  // FAB handlers
  const handleQuickAdd = (type) => {
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setQuickAddType(type);
    setShowQuickAdd(true);
  };

  const handleQuickAddClose = () => {
    setShowQuickAdd(false);
    setQuickAddType(null);
    loadMonthData();
  };

  // Animation variants
  const calendarVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };

  const dayVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: i * 0.01,
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    }),
    tap: { scale: 0.95 }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <motion.div 
          className="flex flex-col items-center gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div 
            className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          <p className="text-gray-500 font-medium">Chargement...</p>
        </motion.div>
      </div>
    );
  }

  const summary = monthSummary || { total_credits: 0, total_debits: 0, final_balance: 0 };

  return (
    <div className="space-y-4 pb-24">
      {/* Header - Premium Clean */}
      <motion.div 
        className="flex items-center justify-between mb-5"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-foreground tracking-tight truncate">
            {calendar.name}
          </h1>
          <p className="text-xs text-muted-foreground">
            {calendar.type === 'joint' ? 'Partagé' : 'Personnel'}
          </p>
        </div>
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setShowNotifications(true)} 
            className="h-9 w-9 rounded-xl relative"
            data-testid="notification-button"
          >
            <Bell className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
            {unreadNotifications > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </Button>
          {calendar.type === 'joint' && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setShowJointAccount(true)} 
              className="h-9 w-9 rounded-xl"
            >
              <Users className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setShowSettings(true)} 
            className="h-9 w-9 rounded-xl" 
            data-testid="settings-button"
          >
            <SettingsIcon className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          </Button>
        </div>
      </motion.div>

      {/* Summary Cards - Réunion Premium Design */}
      <motion.div 
        className="grid grid-cols-3 gap-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        {/* Entrées - Forêt */}
        <motion.div 
          className="card-forest p-4 rounded-2xl text-center"
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <div className="w-7 h-7 rounded-lg icon-forest flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-white" strokeWidth={2} />
            </div>
          </div>
          <div className="text-xl font-extrabold text-emerald-600 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            +{summary.total_credits.toFixed(0)}€
          </div>
          <div className="text-[10px] text-emerald-600/70 uppercase tracking-wider mt-1 font-semibold">Entrées</div>
        </motion.div>
        
        {/* Sorties - Volcan */}
        <motion.div 
          className="card-volcano p-4 rounded-2xl text-center"
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <div className="w-7 h-7 rounded-lg icon-volcano flex items-center justify-center">
              <TrendingDown className="h-4 w-4 text-white" strokeWidth={2} />
            </div>
          </div>
          <div className="text-xl font-extrabold text-rose-500 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            -{summary.total_debits.toFixed(0)}€
          </div>
          <div className="text-[10px] text-rose-500/70 uppercase tracking-wider mt-1 font-semibold">Sorties</div>
        </motion.div>
        
        {/* Solde - Océan */}
        <motion.div 
          className={`p-4 rounded-2xl text-center ${
            summary.final_balance >= 0 
              ? 'card-ocean' 
              : 'bg-gradient-to-br from-rose-500 to-rose-600 shadow-lg'
          }`}
          style={{ boxShadow: summary.final_balance >= 0 ? 'var(--shadow-ocean)' : '0 8px 24px rgba(244, 63, 94, 0.35)' }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Wallet className="h-4 w-4 text-white" strokeWidth={2} />
            </div>
          </div>
          <div className="text-xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {summary.final_balance >= 0 ? '+' : ''}{summary.final_balance.toFixed(0)}€
          </div>
          <div className="text-[10px] text-white/80 uppercase tracking-wider mt-1 font-semibold">Solde</div>
        </motion.div>
      </motion.div>

      {/* Coach message - Subtle Premium */}
      <motion.div 
        className={`text-center py-3 px-4 rounded-2xl text-sm font-medium ${
          summary.final_balance >= 100 ? 'bg-emerald-50 text-emerald-700' :
          summary.final_balance >= 0 ? 'bg-secondary text-secondary-foreground' :
          summary.final_balance >= -100 ? 'bg-amber-50 text-amber-700' :
          'bg-rose-50 text-rose-700'
        }`}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
      >
        {summary.final_balance >= 100 ? 'Excellent ! Tu gères comme un pro' :
         summary.final_balance >= 0 ? 'On garde le cap ! Budget sous contrôle' :
         summary.final_balance >= -100 ? 'Budget tendu, tu peux y arriver !' :
         'Attention, analyse tes dépenses'}
      </motion.div>

      {/* Calendar - Réunion Premium */}
      <motion.div 
        ref={calendarRef}
        className="calendar-container"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {/* Month Navigation - Océan Premium */}
        <div className="calendar-header">
          <div className="flex items-center justify-between">
            <motion.button 
              onClick={handlePrevMonth}
              className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors touch-manipulation"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
            </motion.button>
            
            <AnimatePresence mode="wait" custom={slideDirection}>
              <motion.div 
                key={`${currentDate.getMonth()}-${currentDate.getFullYear()}`}
                className="text-center"
                custom={slideDirection}
                variants={calendarVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              >
                <h2 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', textShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                  {monthNames[currentDate.getMonth()]}
                </h2>
                <p className="text-sm text-white/70">{currentDate.getFullYear()}</p>
              </motion.div>
            </AnimatePresence>
            
            <motion.button 
              onClick={handleNextMonth}
              className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors touch-manipulation"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
            </motion.button>
          </div>
        </div>

        {/* Day names - Premium */}
        <div className="grid grid-cols-7 bg-slate-50/80">
          {dayNames.map((name, i) => (
            <div key={i} className={`py-3 text-center text-xs font-bold uppercase tracking-wider ${
              i === 0 || i === 6 ? 'text-rose-400' : 'text-cyan-700/60'
            }`} style={{ fontFamily: 'Figtree, sans-serif' }}>
              {name}
            </div>
          ))}
        </div>

        {/* Calendar grid - Réunion Premium */}
        <AnimatePresence mode="wait" custom={slideDirection}>
          <motion.div 
            key={`grid-${currentDate.getMonth()}-${currentDate.getFullYear()}`}
            className="calendar-grid grid grid-cols-7 gap-2 p-4"
            custom={slideDirection}
            variants={calendarVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {/* Empty cells for days before the 1st */}
            {Array.from({ length: startingDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}
            
            {/* Days - Réunion Premium Style */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dailyData = getDailyData(day);
              const today = isToday(day);
              const past = isPast(day);
              const hasCredits = dailyData?.credits > 0;
              const hasDebits = dailyData?.debits > 0;
              const hasRecurring = dailyData?.has_recurring;
              const balance = dailyData?.balance;
              const balanceStyle = getBalanceStyle(balance);
              
              return (
                <motion.button
                  key={day}
                  custom={i}
                  variants={dayVariants}
                  initial="hidden"
                  animate="visible"
                  whileTap="tap"
                  onClick={() => handleDayClick(day)}
                  className={`
                    aspect-square rounded-xl relative flex flex-col items-center justify-center
                    transition-all duration-200 touch-manipulation
                    ${today 
                      ? 'calendar-day-today' 
                      : past 
                        ? 'calendar-day opacity-60' 
                        : 'calendar-day hover:scale-102'
                    }
                  `}
                  data-testid={`day-${day}`}
                >
                  {/* Day number */}
                  <span className={`
                    text-sm font-bold
                    ${today ? 'text-cyan-700' : past ? 'text-slate-400' : 'text-slate-700'}
                  `} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {day}
                  </span>
                  
                  {/* Balance indicator */}
                  {balance !== undefined && balance !== null && (
                    <span className={`text-[8px] font-semibold mt-0.5 ${
                      balance >= 0 ? 'text-emerald-500' : 'text-rose-500'
                    }`} style={{ fontFamily: 'Figtree, sans-serif' }}>
                      {balance >= 0 ? '+' : ''}{balance.toFixed(0)}
                    </span>
                  )}
                  
                  {/* Operation indicators */}
                  <div className="flex gap-0.5 mt-0.5 absolute bottom-1.5">
                    {hasCredits && (
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }} />
                    )}
                    {hasDebits && (
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)' }} />
                    )}
                    {hasRecurring && (
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #0369a1 100%)' }} />
                    )}
                  </div>
                </motion.button>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {/* Legend - Réunion Premium */}
        <div className="p-4 bg-slate-50/80 border-t border-slate-100">
          <div className="flex items-center justify-center gap-6 text-[10px] text-slate-500 font-medium">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }} />
              <span>Entrée</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)' }} />
              <span>Sortie</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #0369a1 100%)' }} />
              <span>Récurrent</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Swipe hint - Subtle */}
      <motion.p 
        className="text-center text-[11px] text-muted-foreground/70 mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        Glisse pour changer de mois
      </motion.p>

      {/* Bloc Bilan Mensuel */}
      <BilanMensuel 
        monthSummary={monthSummary}
        currency={calendar.currency}
        monthName={monthNames[currentDate.getMonth()]}
        year={currentDate.getFullYear()}
      />

      {/* Bloc Historique Mensuel */}
      <div className="mt-4">
        <HistoriqueMensuel
          monthSummary={monthSummary}
          currency={calendar.currency}
          monthName={monthNames[currentDate.getMonth()]}
          year={currentDate.getFullYear()}
          onViewFullHistory={onViewFullHistory}
        />
      </div>

      {/* Floating Action Button */}
      <FloatingActionButton
        onAddCredit={() => handleQuickAdd('credit')}
        onAddDebit={() => handleQuickAdd('debit')}
        onAddRecurring={() => handleQuickAdd('recurring')}
      />

      {/* Quick Add Operation Form */}
      {showQuickAdd && quickAddType && quickAddType !== 'recurring' && (
        <OperationForm
          open={true}
          onClose={() => {
            setShowQuickAdd(false);
            setQuickAddType(null);
          }}
          onSuccess={() => {
            loadMonthData();
            toast.success('Opération enregistrée ! ✓');
          }}
          date={selectedDate}
          calendarId={calendar.id}
          currency={calendar.currency}
          defaultType={quickAddType}
        />
      )}

      {/* Quick Add Recurring Form */}
      {showQuickAdd && quickAddType === 'recurring' && (
        <RecurringOperationForm
          open={true}
          onClose={handleQuickAddClose}
          calendarId={calendar.id}
          currency={calendar.currency}
        />
      )}

      {/* Day Detail Modal */}
      {showDayDetail && selectedDate && (
        <DayDetail
          date={selectedDate}
          calendarId={calendar.id}
          currency={calendar.currency}
          onClose={() => {
            setShowDayDetail(false);
            setSelectedDate(null);
            // Always reload month data when closing day detail
            loadMonthData();
          }}
          onOperationChanged={() => {
            loadMonthData();
          }}
        />
      )}

      {/* Settings Modal */}
      <SettingsModal
        open={showSettings}
        onOpenChange={setShowSettings}
        calendar={calendar}
        onSettingsChanged={loadMonthData}
        onLogout={onLogout}
        user={user}
      />

      {/* Notification Center */}
      <NotificationCenter
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        calendarId={calendar?.id}
        onUnreadCountChange={setUnreadNotifications}
      />

      {/* Joint Account Dialog */}
      <JointAccountDialog
        open={showJointAccount}
        onOpenChange={setShowJointAccount}
        calendar={calendar}
      />

      {/* Joint Account Tour */}
      <JointAccountTour
        open={showJointTour}
        onClose={() => setShowJointTour(false)}
        onComplete={handleTourComplete}
      />
    </div>
  );
};

export default BudgetCalendar;
